/**
 * Photo manager (V1 — S-07/S-25 shared): 6 ordered slots, upload through the
 * SM-13d moderation intake (202 → scanning), per-photo status chips
 * (©moderation.photo_pending / photo_rejected states), reorder (arrow-based —
 * same PUT /me/photos/order contract as drag), delete with the last-photo
 * guard surfaced honestly (©profile.need_one_photo).
 */
import { useRef, useState } from "react";
import { copy, resolveCopy } from "@humble/shared";
import { Button, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { AuthedImage } from "./authed-image.js";
import { evictMediaObjectUrl, uploadMultipart } from "./media-api.js";
import "./profile.css";

export interface PhotoItem {
  id: string;
  kind: string;
  status: string;
  position: number | null;
}

export interface PhotoManagerProps {
  photos: PhotoItem[];
  /** Called after any change so the owner refetches /me. */
  onChanged: () => void;
}

const SLOT_COUNT = 6;
const MAX_PHOTO_MB = 10;

function statusChip(status: string): { text: string; cls: string } | null {
  switch (status) {
    case "scanning":
    case "uploaded":
      return { text: "In review", cls: "photo-slot__chip--pending" };
    case "quarantined":
      return { text: "Held for review", cls: "photo-slot__chip--pending" };
    case "live":
      return null;
    default:
      return { text: status, cls: "photo-slot__chip--pending" };
  }
}

export function PhotoManager({ photos, onChanged }: PhotoManagerProps) {
  const [busySlot, setBusySlot] = useState<number | null>(null);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const ordered = [...photos].sort(
    (a, b) => (a.position ?? SLOT_COUNT + 1) - (b.position ?? SLOT_COUNT + 1),
  );
  const byPosition = new Map<number, PhotoItem>();
  for (const p of ordered) {
    if (p.position !== null) byPosition.set(p.position, p);
  }
  const firstFree = (() => {
    for (let pos = 1; pos <= SLOT_COUNT; pos++) if (!byPosition.has(pos)) return pos;
    return null;
  })();

  async function onUpload(position: number, file: File | undefined) {
    if (!file || busySlot !== null) return;
    if (!file.type.startsWith("image/")) {
      toast(`Photos only here — that was ${file.type || "not an image"}. Limit ${MAX_PHOTO_MB} MB.`, "error");
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      toast(`Photo too large — the limit for photos is ${MAX_PHOTO_MB} MB.`, "error");
      return;
    }
    setBusySlot(position);
    try {
      await uploadMultipart("/me/photos", file, { position: String(position) });
      toast(copy.moderation.photo_pending, "info");
      onChanged();
    } catch (err) {
      if (isApiError(err, "RESTRICTED")) {
        toast(resolveCopy("safety.restricted") ?? err.message, "error");
      } else if (isApiError(err)) {
        toast(err.message, "error");
      } else {
        toast("Upload failed — network error.", "error");
      }
    } finally {
      setBusySlot(null);
    }
  }

  async function onDelete(photo: PhotoItem) {
    if (busySlot !== null) return;
    setBusySlot(photo.position ?? 0);
    try {
      await api.delete("/me/photos/{mediaId}", { params: { mediaId: photo.id } });
      evictMediaObjectUrl(photo.id);
      onChanged();
    } catch (err) {
      if (isApiError(err, "LAST_PHOTO")) {
        toast(copy.profile.need_one_photo, "error");
      } else if (isApiError(err)) {
        toast(err.message, "error");
      }
    } finally {
      setBusySlot(null);
    }
  }

  async function onMove(photo: PhotoItem, dir: -1 | 1) {
    const ids = ordered.map((p) => p.id);
    const idx = ids.indexOf(photo.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    try {
      await api.put("/me/photos/order", { body: { order: next } });
      onChanged();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  return (
    <div className="photo-grid" data-testid="photo-grid">
      {Array.from({ length: SLOT_COUNT }, (_, i) => {
        const position = i + 1;
        const photo = byPosition.get(position);
        if (!photo) {
          const enabled = firstFree === position;
          return (
            <div key={position} className="photo-slot photo-slot--empty">
              <input
                ref={(el) => {
                  inputRefs.current[position] = el;
                }}
                id={`photo-input-${position}`}
                data-testid={`photo-input-${position}`}
                type="file"
                accept="image/*"
                className="photo-slot__input"
                disabled={!enabled || busySlot !== null}
                onChange={(e) => {
                  void onUpload(position, e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <label htmlFor={`photo-input-${position}`} className="photo-slot__label">
                {busySlot === position ? "Uploading…" : enabled ? "+ Add photo" : `Slot ${position}`}
              </label>
            </div>
          );
        }
        const chip = statusChip(photo.status);
        return (
          <div key={position} className="photo-slot" data-status={photo.status}>
            <AuthedImage mediaId={photo.id} alt={`Photo ${position}`} />
            {chip && <span className={`photo-slot__chip ${chip.cls}`}>{chip.text}</span>}
            <div className="photo-slot__actions">
              <Button size="sm" variant="ghost" aria-label={`Move photo ${position} earlier`} onClick={() => void onMove(photo, -1)}>
                ←
              </Button>
              <Button size="sm" variant="ghost" aria-label={`Move photo ${position} later`} onClick={() => void onMove(photo, 1)}>
                →
              </Button>
              <Button size="sm" variant="danger" aria-label={`Delete photo ${position}`} onClick={() => void onDelete(photo)}>
                ✕
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
